import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowDown, ArrowRight } from "lucide-react";
import { MaskLines } from "./Motion";
import { scrollToId } from "./SmoothScroll";
import { PHOTOS, CABINET } from "@/lib/content";

const ease = [0.22, 1, 0.36, 1];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.08, 1.2]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-25%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [1.5, -1.5]), { stiffness: 60, damping: 20 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-2, 2]), { stiffness: 60, damping: 20 });

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section id="accueil" ref={ref} onMouseMove={onMove} data-testid="hero-section"
      className="relative h-[100svh] min-h-[640px] overflow-hidden bg-[#1C2826] [perspective:1400px]">
      <motion.div className="absolute inset-0" style={{ y: imgY, rotateX: rx, rotateY: ry }}>
        <motion.img src={PHOTOS.hero.src} alt="Façade du palais de justice d'Aix-en-Provence, siège de la cour d'appel"
          className="h-full w-full object-cover object-[60%_center]" style={{ scale: imgScale }}
          initial={{ opacity: 0, filter: "blur(8px)" }} animate={{ opacity: 1, filter: "blur(0px)" }} transition={{ duration: 1.8, ease }} />
      </motion.div>
      <div className="absolute inset-0 bg-[#16201d]/55" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#16201d]/90 via-transparent to-[#16201d]/40" />
      <div className="grain absolute inset-0" />

      <motion.div style={{ y: textY, opacity: fade }}
        className="relative z-10 mx-auto flex h-full max-w-[1400px] flex-col justify-end px-5 pb-24 sm:px-8 sm:pb-20 lg:px-12">
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3, duration: 1 }}
          className="eyebrow mb-6 flex items-center gap-4 text-[#E5DEC9]">
          <span className="hidden h-px w-10 bg-[#B39158] sm:block" /> Avocate au barreau d'Aix-en-Provence
        </motion.p>
        <h1 data-testid="hero-title" className="font-serif-d max-w-5xl text-[2.6rem] font-light leading-[1.02] tracking-[-0.01em] text-[#FAF8F5] sm:text-6xl lg:text-[5.6rem]">
          <MaskLines animateOnMount delay={0.45} lines={[
            "Défendre les victimes,",
            <>obtenir la <em className="font-normal italic text-[#D9C193]">juste réparation</em></>,
            "de leur préjudice.",
          ]} />
        </h1>
        <div className="mt-10 grid gap-8 lg:grid-cols-12 lg:items-end">
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 1, ease }}
            data-testid="hero-intro" className="max-w-xl text-base font-light leading-relaxed text-[#EDE8DE] sm:text-lg lg:col-span-6">
            Maître Pauline Rège Chanal accompagne les victimes d'accidents et d'agressions, et leurs proches, dans toutes les étapes de leur indemnisation.
            Une défense attentive et exigeante, pour faire reconnaître l'ensemble de vos préjudices.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.3, duration: 1, ease }}
            className="flex flex-wrap items-center gap-5 lg:col-span-6 lg:justify-end">
            <a data-testid="hero-rdv-button" href={CABINET.rdvUrl} target="_blank" rel="noopener noreferrer"
              className="group inline-flex items-center gap-3 rounded-full bg-[#FAF8F5] py-4 pl-7 pr-5 text-[0.95rem] font-medium text-[#1C2826] transition-colors duration-300 hover:bg-[#E5DEC9]">
              Prendre rendez-vous
              <span className="grid h-8 w-8 place-items-center rounded-full bg-[#3D5A4C] text-[#FAF8F5] transition-transform duration-500 group-hover:translate-x-1">
                <ArrowRight size={16} strokeWidth={1.5} />
              </span>
            </a>
            <button data-testid="hero-discover-button" onClick={() => scrollToId("cabinet")}
              className="inline-flex items-center gap-2 text-sm text-[#FAF8F5]/85 transition-colors hover:text-white">
              <ArrowDown size={16} strokeWidth={1.3} className="animate-bounce" /> Découvrir le cabinet
            </button>
          </motion.div>
        </div>
      </motion.div>

      <a href={PHOTOS.hero.source} target="_blank" rel="noopener noreferrer" data-testid="hero-photo-credit"
        className="absolute bottom-4 right-5 z-10 text-[0.72rem] text-[#FAF8F5]/75 hover:text-white sm:right-8 lg:right-12">
        Photo : {PHOTOS.hero.author} · {PHOTOS.hero.license} · Wikimedia Commons
      </a>
    </section>
  );
};
